import { useEffect, useState } from "react";
import { api, errMsg, fmtDate, label } from "../../lib/api";
import { useAuth } from "../../lib/context";
import { Card, Btn, PageHead, Loading } from "../../components/Shell";
import { StatusBadge } from "../../components/StatusBadge";
import { toast } from "sonner";

const Table = ({ cols, rows, testid }) => (
  <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
    <table className="w-full text-sm" data-testid={testid}>
      <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500"><tr>{cols.map((c) => <th key={c} className="px-3 py-2">{c}</th>)}</tr></thead>
      <tbody>{rows.map((r, i) => <tr key={i} className="border-t border-slate-100 align-top">{r.map((c, j) => <td key={j} className="px-3 py-2">{c}</td>)}</tr>)}</tbody>
    </table>
  </div>
);

export function AdminIntegrations() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/integrations").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!d) return <Loading />;
  return (
    <div className="space-y-6">
      <PageHead testid="integrations-title" title="Integration monitoring" sub={`Environment: ${d.environment}. No production bank/provider APIs are connected.`} />
      <Card className="border-purple-300 bg-purple-50" data-testid="integration-blockers">
        <h2 className="mb-2 font-semibold text-purple-900">Integration blockers</h2>
        {d.blockers.map((b) => <div key={b.provider} className="text-sm text-purple-900"><b>{b.provider}</b> ({b.mode}): {b.message}</div>)}
      </Card>
      <h2 className="font-semibold">Capability matrix</h2>
      <Table testid="capability-matrix" cols={["Provider", "Product", "Entity types", "Mode", "Documentation", "Sandbox", "Production", "Missing dependencies"]}
        rows={d.matrix.map((m) => [m.provider, m.product, m.entity_types, m.mode, m.documentation, m.sandbox, m.production_access, m.missing])} />
      <h2 className="font-semibold">Integration requests</h2>
      <Table testid="integration-requests" cols={["Request", "Provider", "Status", "Attempts", "Last error", "Created"]}
        rows={d.requests.map((r) => [<span className="font-mono text-xs">{r.idem_key}</span>, r.provider, <StatusBadge status={r.status} />, r.attempts, r.last_error || "-", fmtDate(r.created_at)])} />
      <h2 className="font-semibold">Webhook events (persisted before processing)</h2>
      <Table testid="webhook-events" cols={["Event", "Type", "Provider ref", "Signature", "Outcome", "Received"]}
        rows={d.webhooks.map((w) => [<span className="font-mono text-xs">{w.event_id}</span>, w.type, <span className="font-mono text-xs">{w.provider_ref}</span>, w.signature_valid ? "valid" : "INVALID", label(w.status), fmtDate(w.received_at)])} />
    </div>
  );
}

export function AdminConfig() {
  const { user } = useAuth();
  const [rs, setRs] = useState(null);
  const [tpl, setTpl] = useState([]);
  const [open, setOpen] = useState(null);
  const load = () => { api.get("/admin/rulesets").then((r) => setRs(r.data)); api.get("/admin/templates").then((r) => setTpl(r.data)); };
  useEffect(load, []);
  const canManage = user.permissions.includes("*") || user.permissions.includes("rules.manage");
  if (!rs) return <Loading />;
  return (
    <div className="space-y-6">
      <PageHead testid="config-title" title="Rules & templates" sub="Document rulesets and form templates are versioned with effective dates and approval status." />
      <h2 className="font-semibold">Document rulesets ({rs.length})</h2>
      <div className="space-y-2">
        {rs.map((r) => (
          <Card key={r.id} className="p-4" data-testid={`ruleset-${r.id}`}>
            <button className="flex w-full flex-wrap items-center gap-2 text-left text-sm" onClick={() => setOpen(open === r.id ? null : r.id)}>
              <span className="font-mono text-xs">{r.id}</span><b>{label(r.provider)}</b>· {label(r.product)} · {label(r.entity)} · v{r.version} · eff. {r.effective_date}
              <span className="ml-auto text-[11px] font-semibold uppercase text-amber-700">{label(r.approval_status)}</span>
            </button>
            {open === r.id && (
              <div className="mt-3 space-y-2 text-sm">
                <div className="text-xs text-slate-500">Source: {r.approved_source} · files {r.file_types.join(", ")} · max {r.max_mb} MB · {r.attestation} · customer {r.customer_type} · residency {r.residency} · channel {r.channel}</div>
                {r.groups.map((g) => <div key={g.code} className="rounded bg-slate-50 p-2"><b>{g.name}</b> {g.required ? "(required" : "(optional"}{g.min > 1 ? `, min ${g.min}` : ""}{g.per_person ? ", per signatory" : ""}{g.max_age_days ? `, ≤${g.max_age_days} days` : ""}) - alternatives: {g.docs.join(", ")}{g.show ? ` · condition ${JSON.stringify(g.show)}` : ""}</div>)}
                {canManage && <Btn variant="outline" data-testid={`ruleset-newver-${r.id}`} onClick={async () => { try { await api.post(`/admin/rulesets/${r.id}/new-version`, { approval_status: "draft" }); toast.success("New draft version created"); load(); } catch (e) { toast.error(errMsg(e)); } }}>Create new draft version</Btn>}
              </div>
            )}
          </Card>
        ))}
      </div>
      <h2 className="font-semibold">Form templates ({tpl.length})</h2>
      <Table testid="templates-table" cols={["Code", "Name", "Provider", "Version", "Effective", "Status", ""]}
        rows={tpl.map((t) => [<span className="font-mono text-xs">{t.code}</span>, t.name, label(t.provider), `v${t.version}`, t.effective_date, label(t.approval_status),
          canManage ? <Btn variant="ghost" onClick={async () => { await api.post(`/admin/templates/${t.id}/new-version`, {}); toast.success("New version"); load(); }}>New version</Btn> : null])} />
    </div>
  );
}

export function AdminAudit() {
  const { user } = useAuth();
  const [logs, setLogs] = useState(null);
  const [users, setUsers] = useState(null);
  const [dups, setDups] = useState(null);
  const admin = user.permissions.includes("*");
  const load = () => {
    api.get("/admin/audit").then((r) => setLogs(r.data));
    if (admin) api.get("/admin/users").then((r) => setUsers(r.data));
    api.get("/admin/duplicates").then((r) => setDups(r.data)).catch(() => {});
  };
  useEffect(load, [admin]);
  if (!logs) return <Loading />;
  return (
    <div className="space-y-6">
      <PageHead testid="audit-title" title="Audit, users & duplicates" />
      {users && (
        <>
          <h2 className="font-semibold">Users & roles</h2>
          <Table testid="users-table" cols={["Email", "Name", "Role", "MFA"]} rows={users.users.map((u) => [u.email, u.name, (
            <select aria-label="Role" data-testid={`role-${u.email}`} className="min-h-[40px] rounded border border-slate-300 px-2 text-sm" value={u.role} disabled={u.user_id === user.user_id}
              onChange={async (e) => { try { await api.patch(`/admin/users/${u.user_id}`, { role: e.target.value }); toast.success("Role updated"); load(); } catch (x) { toast.error(errMsg(x)); } }}>
              {Object.entries(users.roles).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>), u.mfa_enabled ? "enabled" : "-"])} />
        </>
      )}
      {dups && (
        <Card data-testid="duplicates-card"><h2 className="font-semibold">Duplicate-customer review</h2><p className="text-xs text-slate-500">{dups.note}</p>
          {dups.pan_matches.map((m, i) => <div key={i} className="text-sm">{m.match}: {m.businesses.map((b) => `${b.name} (${b.entity})`).join(" / ")}</div>)}
          {dups.document_matches.map((m) => <div key={m.id} className="text-sm">Same file reused across profiles: {m.doc_label} in {m.app_id}</div>)}
          {!dups.pan_matches.length && !dups.document_matches.length && <div className="text-sm text-slate-500">No possible duplicates.</div>}</Card>
      )}
      <h2 className="font-semibold">Audit log</h2>
      <Table testid="audit-table" cols={["Time", "Actor", "Role", "Action", "Entity", "Details"]} rows={logs.map((l) => [fmtDate(l.at), l.actor_email || l.actor_id, l.actor_role, l.action, <span className="font-mono text-xs">{l.entity_type}:{l.entity_id}</span>, <span className="font-mono text-[11px]">{JSON.stringify(l.details)}</span>])} />
    </div>
  );
}
